/**
 * FeedbackButton — reusable thumbs up/down + optional comment popover.
 *
 * v1.1 single-submit (2026-04-25):
 *   - allowComment=true (default): clicking a thumb opens the comment
 *     popover and *defers* submission until the popover closes via any
 *     path (Send / Cancel / X / Esc / click-outside / 30s auto-dismiss).
 *     Exactly one feedback record is POSTed per thumb click.
 *   - allowComment=false: clicking a thumb submits immediately
 *     (polarity-only, no popover).
 *
 *   Rationale (vs the original eager-submit + second-record-with-comment
 *   design): a single record per click avoids the orphan-polarity record
 *   that the analytics pipeline would otherwise need to deduplicate, and
 *   no longer relies on the backend's 5-second dedup window.
 *
 * `inline=true` → always-visible chip styling; default is hover-reveal
 * (parent controls visibility via group-hover).
 */

import { useCallback, useRef, useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFeedback } from "@/hooks/useFeedback";
import type { FeedbackPolarity, FeedbackSurface, FeedbackSurfaceContext } from "@/types/feedback";
import { cn } from "@/lib/utils";
import { FeedbackPopover } from "./FeedbackPopover";

interface FeedbackButtonProps {
  surface: FeedbackSurface;
  /** Per-call context override (merged with ambient FeedbackProvider). */
  context?: FeedbackSurfaceContext;
  /** When false, clicking a thumb does NOT open the comment popover. */
  allowComment?: boolean;
  /** Always-visible chip styling vs. hover-reveal icons. Default: hover-reveal. */
  inline?: boolean;
  /** Compact size for cramped surfaces (e.g. message bubble corners). */
  size?: "sm" | "md";
  /** Label override for the inline chip. */
  label?: string;
  className?: string;
  /** Fired after a record successfully lands (or queues). */
  onSubmitted?: (polarity: FeedbackPolarity) => void;
}

export function FeedbackButton({
  surface,
  context,
  allowComment = true,
  inline = false,
  size = "sm",
  label,
  className,
  onSubmitted,
}: FeedbackButtonProps) {
  const { t } = useTranslation();
  const { submit, isSubmitting } = useFeedback();
  const [pendingPolarity, setPendingPolarity] = useState<FeedbackPolarity | null>(null);
  const [submittedPolarity, setSubmittedPolarity] = useState<FeedbackPolarity | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const doSubmit = useCallback(
    async (polarity: FeedbackPolarity, comment: string | undefined, optedOutOfSanitize: boolean) => {
      await submit({
        polarity,
        surface,
        context,
        comment,
        sanitize: !optedOutOfSanitize,
      });
      setSubmittedPolarity(polarity);
      onSubmitted?.(polarity);
    },
    [submit, surface, context, onSubmitted],
  );

  const handleThumb = useCallback(
    (polarity: FeedbackPolarity, ev: React.MouseEvent) => {
      // Capture anchor position before any state change — element may unmount.
      const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
      setAnchorRect(rect);
      setPendingPolarity(polarity);
      if (allowComment) {
        setPopoverOpen(true);
      } else {
        // No popover — fire submission immediately, polarity-only.
        void doSubmit(polarity, undefined, false);
        setPendingPolarity(null);
      }
    },
    [allowComment, doSubmit],
  );

  const handlePopoverCommit = useCallback(
    (comment: string | undefined, optedOutOfSanitize: boolean) => {
      if (!pendingPolarity) return;
      void doSubmit(pendingPolarity, comment, optedOutOfSanitize);
    },
    [pendingPolarity, doSubmit],
  );

  const closePopover = useCallback(() => {
    setPopoverOpen(false);
    setPendingPolarity(null);
  }, []);

  const iconSize = size === "md" ? 14 : 12;
  const baseBtn = "rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50";
  const padCls = inline ? "px-1.5 py-1" : "p-1";

  // Locked once a polarity has committed (to avoid the user double-submitting
  // mismatched polarities and to make the visual state authoritative).
  // While the popover is open we treat the click as "in flight" and lock too.
  const isLocked =
    submittedPolarity !== null || pendingPolarity !== null || isSubmitting;

  // Highlight whichever polarity is currently committed or pending.
  const highlight = submittedPolarity ?? pendingPolarity;
  const isHighlighted = (p: FeedbackPolarity) => highlight === p;

  return (
    <div ref={containerRef} className={cn("inline-flex items-center gap-0.5", className)}>
      {label && inline && (
        <span className="mr-1 text-[10px] text-slate-400 dark:text-slate-500">{label}</span>
      )}
      <button
        type="button"
        onClick={(ev) => handleThumb("positive", ev)}
        disabled={isLocked}
        title={t("feedback.thumbUpTitle")}
        aria-label={t("feedback.thumbUpAria")}
        aria-pressed={isHighlighted("positive")}
        className={cn(
          baseBtn,
          padCls,
          isHighlighted("positive")
            ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
            : "text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300",
        )}
      >
        <ThumbsUp size={iconSize} />
      </button>
      <button
        type="button"
        onClick={(ev) => handleThumb("negative", ev)}
        disabled={isLocked}
        title={t("feedback.thumbDownTitle")}
        aria-label={t("feedback.thumbDownAria")}
        aria-pressed={isHighlighted("negative")}
        className={cn(
          baseBtn,
          padCls,
          isHighlighted("negative")
            ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
            : "text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300",
        )}
      >
        <ThumbsDown size={iconSize} />
      </button>

      <FeedbackPopover
        open={popoverOpen}
        onCommit={handlePopoverCommit}
        onClose={closePopover}
        anchorRect={anchorRect}
      />
    </div>
  );
}
